/* Spring Step Shoes — global refreshed 01 preference / duplicate guard (Sep 16, 2026).
   Applies the established image logic to every product rendered by the BIS theme:
   - same-color family only
   - prefer a replacement/suffixed 01 or an explicitly labeled Front view
   - keep 05 when no valid same-family 01 exists
   - place the preferred 01 ahead of legacy duplicate 01 media before the PDP filter runs
   - never use the old delayed PLP-wide 05→01 enforcer. */
(function () {
  const PDP_MEDIA_ROOTS = [
    '.product-section [data-section^="product-media-"]',
    'product-modal'
  ];

  const filenameFromSrc = (src) => {
    if (!src) return '';
    try {
      return decodeURIComponent(new URL(src, window.location.origin).pathname.split('/').pop() || '');
    } catch (error) {
      return String(src).split('?')[0].split('/').pop() || '';
    }
  };

  const srcFromElement = (element) => {
    const img = element?.matches?.('img') ? element : element?.querySelector?.('img');
    return img?.currentSrc || img?.src || img?.getAttribute?.('src') || '';
  };

  const mediaCore = (src) => {
    const file = filenameFromSrc(src).replace(/\.[a-z0-9]+$/i, '');
    return file.split('_')[0];
  };

  const mediaView = (src) => {
    const match = mediaCore(src).match(/-(\d{2}|ONFOOT)$/i);
    return match ? match[1].toUpperCase() : '';
  };

  const mediaFamily = (src) => mediaCore(src)
    .replace(/-(?:\d{2}|ONFOOT)$/i, '')
    .replace(/^BALDWIN-CAMP$/i, 'BALDWIN-CAP');

  const isReplacement01 = (img) => {
    if (!img) return false;
    const filename = filenameFromSrc(img.currentSrc || img.src || img.getAttribute('src') || '');
    const alt = (img.getAttribute('alt') || '').trim();
    return /\bfront view\b/i.test(alt) || /-01_[^/.]+\.[a-z0-9]+$/i.test(filename);
  };

  const normalize01Image = (img) => {
    if (!img) return;
    const src = img.currentSrc || img.src || img.getAttribute('src') || '';
    if (mediaView(src) !== '01') return;
    if (/-01_[^/.]+\.[a-z0-9]+$/i.test(filenameFromSrc(src))) {
      img.dataset.sssRefreshed01 = 'true';
      if (!(img.getAttribute('alt') || '').trim()) img.setAttribute('alt', 'Front view');
    }
  };

  const normalizeAll01Images = (root = document) => {
    root.querySelectorAll?.('img').forEach(normalize01Image);
  };

  const reorderDuplicate01sInContainer = (container) => {
    if (!container?.children?.length) return;
    const children = Array.from(container.children).filter((el) => el instanceof HTMLElement);
    const families = new Map();

    children.forEach((element) => {
      const src = srcFromElement(element);
      if (!src || mediaView(src) !== '01') return;
      const family = mediaFamily(src);
      if (!family) return;
      if (!families.has(family)) families.set(family, []);
      families.get(family).push(element);
    });

    families.forEach((items) => {
      if (items.length < 2) return;
      const preferred = items.find((element) => isReplacement01(element.querySelector('img')));
      if (!preferred) return;
      const first01 = items[0];
      if (preferred === first01) return;
      container.insertBefore(preferred, first01);
    });
  };

  const normalizePdpOrder = (root = document) => {
    normalizeAll01Images(root);
    PDP_MEDIA_ROOTS.forEach((selector) => {
      root.querySelectorAll?.(selector).forEach((mediaRoot) => {
        mediaRoot.querySelectorAll?.('.product__media-list, .js-media-sublist > .swiper-wrapper, .js-popup-slider > .swiper-wrapper, .js-popup-sublist > .swiper-wrapper').forEach((candidate) => {
          const container = candidate.matches('.product__media-list')
            ? (candidate.querySelector(':scope > .swiper-wrapper') || candidate)
            : candidate;
          reorderDuplicate01sInContainer(container);
        });
      });
    });
  };

  normalizePdpOrder();

  const start = () => normalizePdpOrder();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  }

  const observer = new MutationObserver((mutations) => {
    let relevant = false;
    for (const mutation of mutations) {
      if (mutation.addedNodes?.length) {
        relevant = true;
        break;
      }
    }
    if (relevant) normalizePdpOrder();
  });

  const observe = () => {
    if (document.body) observer.observe(document.body, { childList: true, subtree: true });
  };
  if (document.body) observe();
  else document.addEventListener('DOMContentLoaded', observe, { once: true });
})();
